import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaUsers, FaCalendarPlus, FaCheck, FaTimes, FaMapMarkerAlt, FaVideo } from "react-icons/fa";
import { AppCard, AppButton, Badge, IconBox } from "../components/app";
import { appColors, appType, appRadius } from "../utils/appTheme";
import { ASAMBLEA_PUBLICO_URL, entrarPublico, enVentana, horaMontevideo, ventanaEntrar } from "../utils/asambleas/api";
import { useAhoraEnLimites } from "../utils/asambleas/reloj";
import { enlaceGoogleCalendar, enlaceOutlook, textoDelEvento } from "../utils/asambleas/calendario";
import { fechaHoraAsamblea, normalizarIdioma } from "../utils/asambleas/idioma";

const ESTADOS_CON_ENTRADA = new Set(["convocada", "en_curso"]);

// La fecha va sin tabular-nums: en FT Habit esa feature cambia "," y ":" por
// glifos de ancho fijo (600 u. contra ~240) y la fecha se ve con espacios de más.
const estiloFecha = { ...appType.stat, color: appColors.ink, fontVariantNumeric: "normal" };

const enlaceComoBoton = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "11px 14px",
  borderRadius: appRadius.button,
  border: `1px solid ${appColors.line}`,
  color: appColors.ink,
  background: "transparent",
  fontFamily: appType.fontFamily,
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1,
  textDecoration: "none",
  transition: "background .15s ease, border-color .15s ease",
};

/**
 * Enlace personal de una asamblea (/asamblea/:t), sin iniciar sesión: llega en
 * el botón "Ver detalle" del WhatsApp o en el email. Muestra la asamblea,
 * registra la asistencia y ofrece agregarla al calendario.
 *
 * Videollamada:
 * - con link manual (video.proveedor "manual"): de T-15 al cierre de la sala,
 *   "Entrar a la asamblea" pide el link con POST { op: "entrar", t } (es el
 *   mismo para todos, así que la citación alcanza) y lo abre en una pestaña
 *   que se abre en el mismo toque. El link no se guarda. Si no sale, dice por
 *   qué (todavía no, sin link, terminada, suspendida, acceso no disponible:
 *   el módulo apagado) y solo "Probá de nuevo" cuando reintentar sirve;
 * - con Zoom automático: la citación no da el acceso personal (ese llega 15
 *   minutos antes en su propio enlace /z/), así que solo se avisa eso.
 *
 * Idioma: el del propietario que manda el servidor (`idioma`: es | en |
 * pt-BR) y, si no viene, español. Nunca el del navegador ni el que quedó
 * guardado en el dispositivo (i18n.language): el enlace se abre en cualquier
 * teléfono. Mientras carga y en los errores (sin respuesta), español.
 */
export default function AsambleaPublica() {
  const { t: token } = useParams();
  const { i18n } = useTranslation("asambleas");
  const [estado, setEstado] = useState("cargando"); // cargando | ok | vencido | no_encontrado | error
  const [data, setData] = useState(null);
  // Idioma que manda el servidor también en un enlace vencido o sin respuesta válida (410/404 con cuerpo).
  const [idiomaError, setIdiomaError] = useState(null);
  const idioma = data
    ? normalizarIdioma(data.idioma || (data.invitacion && data.invitacion.idioma))
    : normalizarIdioma(idiomaError);
  const t = useMemo(() => i18n.getFixedT(idioma, "asambleas"), [i18n, idioma]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  // Videollamada con link manual: solo en memoria, para el botón de respaldo.
  const [acceso, setAcceso] = useState(null);
  const [entrando, setEntrando] = useState(false);
  const [errorEntrar, setErrorEntrar] = useState("");

  const cargar = useCallback(async () => {
    try {
      const resp = await fetch(`${ASAMBLEA_PUBLICO_URL}?t=${encodeURIComponent(token)}`);
      if (!resp.ok) {
        const cuerpo = await resp.json().catch(() => null);
        setIdiomaError((cuerpo && cuerpo.idioma) || null);
        return setEstado(resp.status === 410 ? "vencido" : "no_encontrado");
      }
      setData(await resp.json());
      setEstado("ok");
    } catch (e) {
      setEstado("error");
    }
  }, [token]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Ventana de la videollamada (T-15 al cierre de la sala): la página se
  // actualiza sola en esos bordes, sin volver a consultar.
  const asm = data && data.asamblea ? data.asamblea : null;
  const proveedor = asm && asm.video ? asm.video.proveedor : null;
  const conVideo =
    !!asm && !!(data && data.invitacion) && asm.modalidad !== "presencial" && ESTADOS_CON_ENTRADA.has(asm.estado) &&
    (proveedor === "manual" || proveedor === "zoom");
  const ventana = conVideo ? ventanaEntrar(asm) : null;
  const ahora = useAhoraEnLimites(ventana ? [ventana.desdeMs, ventana.hastaMs, Number(asm.inicioMs)] : asm ? [Number(asm.inicioMs)] : []);

  const responder = async (respuesta) => {
    setGuardando(true);
    setError("");
    try {
      const resp = await fetch(ASAMBLEA_PUBLICO_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ t: token, respuesta, version: data.asamblea.version }),
      });
      if (!resp.ok) throw new Error(String(resp.status));
      await cargar();
    } catch (e) {
      setError(t("asistencia.error"));
    } finally {
      setGuardando(false);
    }
  };

  const entrarAhora = async () => {
    setErrorEntrar("");
    // La pestaña se abre en el mismo toque (antes del await) para que el navegador
    // no la bloquee; el link se carga ahí cuando llega.
    let pestana = null;
    try {
      pestana = window.open("", "_blank");
    } catch (_) {
      pestana = null;
    }
    if (pestana) {
      try {
        pestana.opener = null;
      } catch (_) {
        /* algunos navegadores no lo dejan cambiar */
      }
    }
    setEntrando(true);
    try {
      const res = await entrarPublico(token);
      if (res.joinUrl) {
        setAcceso({ joinUrl: res.joinUrl });
        if (pestana && !pestana.closed) pestana.location.href = res.joinUrl;
        return;
      }
      if (pestana && !pestana.closed) {
        try {
          pestana.close();
        } catch (_) {
          /* ya cerrada */
        }
      }
      if (res.todavia) {
        // Reloj del teléfono adelantado: el servidor dice desde qué hora.
        setErrorEntrar(res.habilitaEnMs ? t("entrar.habilitaA", { hora: horaMontevideo(res.habilitaEnMs) }) : t("zoomPublico.todaviaSinHora"));
      } else if (res.error === "sin_video") {
        setErrorEntrar(t("entrar.sinLink"));
      } else if (res.error === "terminada") {
        setErrorEntrar(t("entrar.terminada"));
      } else if (res.error === "cancelada") {
        // Se suspendió después de que se cargó la página.
        setErrorEntrar(t("entrar.cancelada"));
      } else if (res.error === "no_disponible") {
        // Módulo apagado (el rollback): reintentar no lo arregla.
        setErrorEntrar(t("entrar.noDisponible"));
      } else if (res.error === "sin_acceso") {
        setErrorEntrar(t("entrar.sinAcceso"));
      } else {
        setErrorEntrar(t("entrar.error"));
      }
    } finally {
      setEntrando(false);
    }
  };

  const contenedor = { minHeight: "100vh", background: appColors.cream, display: "flex", justifyContent: "center", padding: "24px 16px" };
  const etiqueta = { ...appType.statLabel, color: appColors.ink3, marginBottom: 3 };

  if (estado !== "ok") {
    const textos = {
      cargando: t("loading"),
      vencido: t("publico.vencido"),
      no_encontrado: t("publico.noEncontrado"),
      error: t("publico.error"),
    };
    return (
      <main style={contenedor} lang={idioma}>
        <div style={{ maxWidth: 520, width: "100%", ...appType.body, color: appColors.ink3, textAlign: "center", paddingTop: 48 }}>{textos[estado]}</div>
      </main>
    );
  }

  const a = data.asamblea;
  const inv = data.invitacion;
  const abierta = a.estado === "convocada" && Number(a.inicioMs) > ahora;
  const rsvp = inv ? inv.rsvp : null;
  const texto = textoDelEvento(a, a.edificio ? a.edificio.nombre : "", t);
  const urlIcs = `${ASAMBLEA_PUBLICO_URL}?t=${encodeURIComponent(token)}&formato=ics`;
  const conLink = conVideo && proveedor === "manual";
  const conZoom = conVideo && proveedor === "zoom";
  const puedeEntrar = conLink && enVentana(ventana, ahora);
  const antesDeEntrar = conLink && !!ventana && ahora < ventana.desdeMs;
  const avisoZoom = conZoom && !!ventana && ahora < ventana.hastaMs;

  return (
    <main style={contenedor} lang={idioma}>
      <div style={{ maxWidth: 560, width: "100%" }}>
        <AppCard padding={0} style={{ overflow: "hidden" }}>
          <div style={{ padding: "18px 18px 0", display: "flex", gap: 12, alignItems: "flex-start" }}>
            <IconBox icon={<FaUsers />} color={appColors.blue} size={44} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...appType.statLabel, color: appColors.blue, marginBottom: 3 }}>{a.edificio ? a.edificio.nombre : ""}</div>
              <div style={{ ...appType.cardTitle, fontSize: 17 }}>{t(`tipo.${a.tipo}`)}</div>
              {inv && inv.nombre && <div style={{ ...appType.body, color: appColors.ink3, marginTop: 2 }}>{t("publico.hola", { nombre: inv.nombre })}</div>}
            </div>
            <Badge tone={a.estado === "cancelada" ? "red" : "blue"} dot>{t(`estado.${a.estado}`)}</Badge>
          </div>

          <div style={{ padding: "12px 18px 0" }}>
            <div style={{ background: appColors.cream, borderRadius: appRadius.tile, padding: "12px 14px" }}>
              <div style={etiqueta}>{t("fechaHora")}</div>
              <div style={estiloFecha}>{fechaHoraAsamblea(a.inicioMs, idioma)}</div>
              <div style={{ ...appType.body, fontSize: 13, color: appColors.ink3, marginTop: 4 }}>
                {t("modalidad." + a.modalidad)}
              </div>
            </div>
          </div>

          <div style={{ padding: "12px 18px 0", display: "flex", flexDirection: "column", gap: 10 }}>
            {a.modalidad !== "remota" && a.lugar && (
              <div style={{ ...appType.body, display: "flex", alignItems: "center", gap: 6 }}>
                <FaMapMarkerAlt style={{ color: appColors.blue, fontSize: 12 }} /> {a.lugar}
              </div>
            )}
            <div>
              <div style={etiqueta}>{t("ordenDelDia")}</div>
              <ol style={{ margin: 0, paddingLeft: 18, ...appType.body }}>
                {(a.ordenDelDia || []).map((p) => (
                  <li key={p.id} style={{ marginBottom: 4 }}>
                    {p.titulo}
                    {p.detalle && <div style={{ fontSize: 13, color: appColors.ink3 }}>{p.detalle}</div>}
                  </li>
                ))}
              </ol>
            </div>
            {inv && inv.unidades && inv.unidades.length > 0 && (
              <div>
                <div style={etiqueta}>{t("tusUnidades")}</div>
                <div style={appType.body}>{inv.unidades.join(", ")}</div>
              </div>
            )}
            {a.estado === "cancelada" && a.motivoCancelacion && (
              <div style={{ ...appType.body, color: appColors.red }}>{t("motivoCancelacion", { motivo: a.motivoCancelacion })}</div>
            )}
          </div>

          {(puedeEntrar || antesDeEntrar || avisoZoom) && (
            <div style={{ padding: "12px 18px 0", display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={etiqueta}>{t("entrar.titulo")}</div>
              {avisoZoom && <div style={{ ...appType.body, color: appColors.ink2 }}>{t("entrar.zoomLlega")}</div>}
              {antesDeEntrar && (
                <div style={{ ...appType.body, color: appColors.ink2 }}>{t("entrar.habilitaA", { hora: horaMontevideo(ventana.desdeMs) })}</div>
              )}
              {puedeEntrar && (
                <AppButton variant="primary" block icon={<FaVideo size={13} />} disabled={entrando} onClick={entrarAhora}>
                  {entrando ? t("entrar.abriendo") : t("entrar.boton")}
                </AppButton>
              )}
              {puedeEntrar && acceso && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ ...appType.body, fontSize: 13, color: appColors.ink3 }}>{t("entrar.noAbrioUno")}</div>
                  <div>
                    <a href={acceso.joinUrl} target="_blank" rel="noopener noreferrer" style={enlaceComoBoton}>
                      {t("entrar.abrir")}
                    </a>
                  </div>
                  <div style={{ ...appType.body, fontSize: 12, color: appColors.ink3 }}>{t("entrar.compartido")}</div>
                </div>
              )}
              {errorEntrar && (
                <div role="alert" style={{ ...appType.body, color: appColors.red }}>
                  {errorEntrar}
                </div>
              )}
            </div>
          )}

          <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 10 }}>
            {abierta && inv && (
              <>
                {rsvp ? (
                  <div style={{ ...appType.body, display: "flex", alignItems: "center", gap: 8, color: rsvp === "si" ? appColors.green : appColors.ink3 }}>
                    {rsvp === "si" ? <FaCheck /> : <FaTimes />} {rsvp === "si" ? t("asistencia.confirmadoSi") : t("asistencia.confirmadoNo")}
                  </div>
                ) : (
                  <div style={etiqueta}>{t("asistencia.label")}</div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <AppButton variant={rsvp === "si" ? "outline" : "primary"} block disabled={guardando || rsvp === "si"} onClick={() => responder("si")}>
                    {t("asistencia.si")}
                  </AppButton>
                  <AppButton variant="outline" block disabled={guardando || rsvp === "no"} onClick={() => responder("no")}>
                    {t("asistencia.no")}
                  </AppButton>
                </div>
              </>
            )}
            {a.estado !== "cancelada" && (
              <div>
                <div style={etiqueta}>{t("calendario.label")}</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <AppButton variant="outline" icon={<FaCalendarPlus size={13} />}
                    onClick={() => window.open(enlaceGoogleCalendar(a, texto), "_blank", "noopener,noreferrer")}>
                    {t("calendario.google")}
                  </AppButton>
                  <AppButton variant="outline" icon={<FaCalendarPlus size={13} />}
                    onClick={() => window.open(enlaceOutlook(a, texto), "_blank", "noopener,noreferrer")}>
                    {t("calendario.outlook")}
                  </AppButton>
                  <AppButton variant="outline" icon={<FaCalendarPlus size={13} />} onClick={() => { window.location.href = urlIcs; }}>
                    {t("calendario.ics")}
                  </AppButton>
                </div>
              </div>
            )}
            {error && <div role="alert" style={{ ...appType.body, color: appColors.red }}>{error}</div>}
          </div>
        </AppCard>
        <div style={{ ...appType.body, fontSize: 12, color: appColors.ink3, textAlign: "center", marginTop: 16 }}>
          {t("publico.personal")}
        </div>
      </div>
    </main>
  );
}
